// "How it works": the product's three stages, documents → wiki → capsules, each with the
// `collect` command that runs it (the CLI package, WP-C15; --encrypt is experimental, DR-210,
// so it is never the stage's command). Sources: apps/cli/collect.py (ingest, llm_analyze_and_generate,
// build_capsule_bundle, send-to-hive), apps/cli/encryption.py, apps/web/src/lib/bundleBuilder.ts,
// apps/extension/background.js. Stage colours match the background flow (Landing FLOW).
import { motion } from "motion/react";
import { useLandingTheme } from "./theme";
import { FileText, Network, Hexagon } from "lucide-react";

export const STAGES = [
  {
    n: "01",
    name: "Documents",
    title: "Bring in a file.",
    color: "#10B981",
    deep: "#064E3B",
    icon: FileText,
    motif: "heartbeat",
    command: "collect ingest my-wiki notes.md",
    desc: "Markdown and text go in as they are; other formats are converted to Markdown with markitdown. Text files dropped on the Send page and pages saved with the extension go straight into a capsule as sources — the wiki step below runs in the CLI.",
    line: "File → Markdown source",
  },
  {
    n: "02",
    name: "Wiki",
    title: "The wiki writes itself.",
    color: "#BB86FC",
    deep: "#2E1065",
    icon: Network,
    motif: "drift",
    command: "summaries/ · concepts/ · kg/triples.json",
    desc: "The LLM reads your purpose.md, writes a summary that cites its source, creates or updates concept pages and adds subject–predicate–object triples to the knowledge graph. Every ingest is a git commit; search the wiki with BM25.",
    line: "Source → summary · concepts · graph",
  },
  {
    n: "03",
    name: "Capsules",
    title: "Send a capsule.",
    color: "#F2C94C",
    deep: "#0D1B2A",
    icon: Hexagon,
    motif: "stream",
    command: "collect send-to-hive my-wiki",
    desc: "Sources, summaries, concepts and triples are bundled (collect.capsule.v1), hashed with SHA-256, gzipped and recorded in your HIVE. Experimental, in the CLI only: --encrypt seals the bundle with ML-KEM-768 + AES-256-GCM.",
    line: "Workspace → HIVE",
  },
] as const;

/** Commands, flags and file names inside a description: monospace, never split across lines. */
const CODE = /(--encrypt|purpose\.md|collect\.capsule\.v1|markitdown)/;
function rich(text: string) {
  return text.split(CODE).map((part, i) =>
    i % 2 ? <span key={i} className="font-mono text-[0.88em] whitespace-nowrap">{part}</span> : part,
  );
}

export function ProcessFlow() {
  const { tone, theme } = useLandingTheme();
  return (
    <section id="how" aria-labelledby="how-title" className="relative px-6 md:px-12 py-24 md:py-32 border-t border-white/10">
      {/* One soft glow per stage */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40" aria-hidden="true">
        <div className="absolute top-10 left-[10%] w-[40vw] h-[40vw] rounded-full" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 70%)", filter: "blur(50px)" }} />
        <div className="absolute top-[30%] left-[35%] w-[40vw] h-[40vw] rounded-full" style={{ background: "radial-gradient(circle, rgba(187,134,252,0.14) 0%, transparent 70%)", filter: "blur(50px)" }} />
        <div className="absolute bottom-10 right-[10%] w-[40vw] h-[40vw] rounded-full" style={{ background: "radial-gradient(circle, rgba(242,201,76,0.12) 0%, transparent 70%)", filter: "blur(50px)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto z-10">
        <header className="mb-16 md:mb-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-4">How it works</p>
          <h2 id="how-title" className="text-5xl md:text-7xl font-serif font-light leading-[0.95]">
            Documents in.<br />
            <span className="italic text-white/65">Capsules out.</span>
          </h2>
        </header>

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
          {STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            return (
              <motion.li
                key={stage.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.15, duration: 0.6 }}
                className="relative rounded-3xl border p-7 md:p-8 lg:p-10 flex flex-col backdrop-blur-md"
                style={{
                  borderColor: `${stage.color}30`,
                  // light theme: a tint of the stage colour over white, not the dark "deep" (keeps the
                  // number AA); both themes blur the background flow behind the text.
                  background: theme === "light"
                    ? `linear-gradient(180deg, ${stage.color}14 0%, rgba(255,255,255,0.62) 100%)`
                    : `linear-gradient(180deg, ${stage.deep}66 0%, rgba(5,5,5,0.55) 100%)`,
                }}
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-xs tracking-[0.3em]" style={{ color: tone(stage.color) }}>
                    {stage.n} · {stage.name}
                  </span>
                  <span
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: `radial-gradient(circle, ${stage.color}25 0%, transparent 70%)` }}
                    aria-hidden="true"
                  >
                    <Icon size={34} strokeWidth={1} style={{ color: tone(stage.color) }} className={stage.motif} />
                  </span>
                </div>

                <h3 className="text-3xl md:text-[2rem] lg:text-4xl font-serif font-light mb-5" style={{ color: tone(stage.color) }}>
                  {stage.title}
                </h3>
                <p className="text-sm md:text-base font-light text-white/70 leading-relaxed mb-6">{rich(stage.desc)}</p>
                <code className="collect-code block mt-auto mb-6 px-3 py-2 rounded-lg text-xs">{stage.command}</code>

                <div
                  className="font-mono text-[10px] tracking-[0.2em] uppercase pt-4 border-t"
                  style={{ color: tone(stage.color), borderColor: `${stage.color}25` }}
                >
                  {stage.line}
                </div>

                {idx < STAGES.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 z-20 text-2xl" style={{ color: tone(STAGES[idx + 1].color) }} aria-hidden="true">
                    →
                  </div>
                )}
              </motion.li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
